"use client";

import { useTranslation } from "react-i18next";
import { FooterSection } from "@/components/marketing/FooterSection";

type TermsSection = {
  id: string;
  headingKey: string;
  paragraphKeys: readonly string[];
  bulletKeys?: readonly string[];
};

const TERMS_SECTIONS: readonly TermsSection[] = [
  {
    id: "scope",
    headingKey: "TERMS_SCOPE_HEADING",
    paragraphKeys: ["TERMS_SCOPE_TEXT_1", "TERMS_SCOPE_TEXT_2"],
  },
  {
    id: "eligibility",
    headingKey: "TERMS_ELIGIBILITY_HEADING",
    paragraphKeys: ["TERMS_ELIGIBILITY_TEXT_1", "TERMS_ELIGIBILITY_TEXT_2"],
  },
  {
    id: "accounts",
    headingKey: "TERMS_ACCOUNT_SECURITY_HEADING",
    paragraphKeys: ["TERMS_ACCOUNT_SECURITY_TEXT_1", "TERMS_ACCOUNT_SECURITY_TEXT_2"],
  },
  {
    id: "service",
    headingKey: "TERMS_SERVICE_DESCRIPTION_HEADING",
    paragraphKeys: ["TERMS_SERVICE_DESCRIPTION_TEXT_1", "TERMS_SERVICE_DESCRIPTION_TEXT_2"],
  },
  {
    id: "user-content",
    headingKey: "TERMS_USER_CONTENT_HEADING",
    paragraphKeys: ["TERMS_USER_CONTENT_TEXT_1", "TERMS_USER_CONTENT_TEXT_2"],
  },
  {
    id: "ai-features",
    headingKey: "TERMS_AI_FEATURES_HEADING",
    paragraphKeys: ["TERMS_AI_FEATURES_TEXT_1", "TERMS_AI_FEATURES_TEXT_2"],
  },
  {
    id: "third-party",
    headingKey: "TERMS_THIRD_PARTY_SERVICES_HEADING",
    paragraphKeys: ["TERMS_THIRD_PARTY_SERVICES_TEXT_1", "TERMS_THIRD_PARTY_SERVICES_TEXT_2"],
  },
  {
    id: "acceptable-use",
    headingKey: "TERMS_ACCEPTABLE_USE_HEADING",
    paragraphKeys: ["TERMS_ACCEPTABLE_USE_TEXT_1", "TERMS_ACCEPTABLE_USE_TEXT_2"],
    bulletKeys: [
      "TERMS_ACCEPTABLE_USE_ITEM_1",
      "TERMS_ACCEPTABLE_USE_ITEM_2",
      "TERMS_ACCEPTABLE_USE_ITEM_3",
      "TERMS_ACCEPTABLE_USE_ITEM_4",
      "TERMS_ACCEPTABLE_USE_ITEM_5",
      "TERMS_ACCEPTABLE_USE_ITEM_6",
      "TERMS_ACCEPTABLE_USE_ITEM_7",
      "TERMS_ACCEPTABLE_USE_ITEM_8",
      "TERMS_ACCEPTABLE_USE_ITEM_9",
      "TERMS_ACCEPTABLE_USE_ITEM_10",
    ],
  },
  {
    id: "billing",
    headingKey: "TERMS_BILLING_HEADING",
    paragraphKeys: ["TERMS_BILLING_TEXT_1", "TERMS_BILLING_TEXT_2"],
  },
  {
    id: "refunds",
    headingKey: "TERMS_REFUNDS_HEADING",
    paragraphKeys: ["TERMS_REFUNDS_TEXT_1", "TERMS_REFUNDS_TEXT_2"],
  },
  {
    id: "limits",
    headingKey: "TERMS_CREDITS_LIMITS_HEADING",
    paragraphKeys: ["TERMS_CREDITS_LIMITS_TEXT_1", "TERMS_CREDITS_LIMITS_TEXT_2"],
  },
  {
    id: "ip",
    headingKey: "TERMS_IP_HEADING",
    paragraphKeys: ["TERMS_IP_TEXT_1", "TERMS_IP_TEXT_2"],
  },
  {
    id: "feedback",
    headingKey: "TERMS_FEEDBACK_LICENSE_HEADING",
    paragraphKeys: ["TERMS_FEEDBACK_LICENSE_TEXT_1", "TERMS_FEEDBACK_LICENSE_TEXT_2"],
  },
  {
    id: "privacy",
    headingKey: "TERMS_PRIVACY_DATA_HEADING",
    paragraphKeys: ["TERMS_PRIVACY_DATA_TEXT_1", "TERMS_PRIVACY_DATA_TEXT_2"],
  },
  {
    id: "security",
    headingKey: "TERMS_SECURITY_INCIDENTS_HEADING",
    paragraphKeys: ["TERMS_SECURITY_INCIDENTS_TEXT_1", "TERMS_SECURITY_INCIDENTS_TEXT_2"],
  },
  {
    id: "availability",
    headingKey: "TERMS_AVAILABILITY_CHANGES_HEADING",
    paragraphKeys: ["TERMS_AVAILABILITY_CHANGES_TEXT_1", "TERMS_AVAILABILITY_CHANGES_TEXT_2"],
  },
  {
    id: "termination",
    headingKey: "TERMS_SUSPENSION_TERMINATION_HEADING",
    paragraphKeys: ["TERMS_SUSPENSION_TERMINATION_TEXT_1", "TERMS_SUSPENSION_TERMINATION_TEXT_2"],
  },
  {
    id: "indemnification",
    headingKey: "TERMS_INDEMNIFICATION_HEADING",
    paragraphKeys: ["TERMS_INDEMNIFICATION_TEXT_1", "TERMS_INDEMNIFICATION_TEXT_2"],
  },
  {
    id: "disclaimer",
    headingKey: "TERMS_DISCLAIMER_HEADING",
    paragraphKeys: ["TERMS_DISCLAIMER_TEXT_1", "TERMS_DISCLAIMER_TEXT_2"],
  },
  {
    id: "liability",
    headingKey: "TERMS_LIMITATION_HEADING",
    paragraphKeys: ["TERMS_LIMITATION_TEXT_1", "TERMS_LIMITATION_TEXT_2"],
  },
  {
    id: "law",
    headingKey: "TERMS_DISPUTES_GOVERNING_LAW_HEADING",
    paragraphKeys: ["TERMS_DISPUTES_GOVERNING_LAW_TEXT_1", "TERMS_DISPUTES_GOVERNING_LAW_TEXT_2"],
  },
  {
    id: "changes",
    headingKey: "TERMS_CHANGES_TO_TERMS_HEADING",
    paragraphKeys: ["TERMS_CHANGES_TO_TERMS_TEXT_1", "TERMS_CHANGES_TO_TERMS_TEXT_2"],
  },
  {
    id: "contact",
    headingKey: "TERMS_CONTACT_HEADING",
    paragraphKeys: ["TERMS_CONTACT_TEXT_1", "TERMS_CONTACT_TEXT_2"],
  },
];

export default function TermsContent() {
  const { t } = useTranslation();

  return (
    <main className="mx-auto max-w-7xl px-5 py-12">
      <h1 className="mb-2 text-2xl font-bold md:text-3xl">{t("TERMS_TITLE_TEXT")}</h1>
      <p className="mb-8 text-sm text-muted-foreground">{t("TERMS_LAST_UPDATED_TEXT")}</p>

      <section className="mb-8 space-y-4 text-sm leading-6 text-muted-foreground">
        <p>{t("TERMS_INTRO_TEXT_1")}</p>
        <p>{t("TERMS_INTRO_TEXT_2")}</p>
      </section>

      <nav
        aria-label={t("TERMS_TABLE_OF_CONTENTS_ARIA_LABEL")}
        className="mb-10 rounded-xl border bg-card/40 p-4 text-sm"
      >
        <h2 className="mb-3 text-base font-semibold text-foreground">{t("TERMS_TABLE_OF_CONTENTS_TITLE_TEXT")}</h2>
        <ol className="grid gap-2 md:grid-cols-2">
          {TERMS_SECTIONS.map((section) => (
            <li key={section.id}>
              <a className="text-muted-foreground transition-colors hover:text-foreground" href={`#${section.id}`}>
                {t(section.headingKey)}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <section className="space-y-7 text-sm leading-6 text-muted-foreground">
        {TERMS_SECTIONS.map((section) => (
          <article className="scroll-mt-24 space-y-3" id={section.id} key={section.id}>
            <h2 className="text-lg font-semibold text-foreground">{t(section.headingKey)}</h2>
            {section.paragraphKeys.map((paragraphKey) => (
              <p key={paragraphKey}>{t(paragraphKey)}</p>
            ))}
            {section.bulletKeys ? (
              <ul className="list-disc space-y-1 pl-5">
                {section.bulletKeys.map((bulletKey) => (
                  <li key={bulletKey}>{t(bulletKey)}</li>
                ))}
              </ul>
            ) : null}
          </article>
        ))}
      </section>
      <div className="mt-10">
        <FooterSection />
      </div>
    </main>
  );
}
